import * as yup from 'yup'
import { addDays, addWeeks, addYears, format, isValid, parse, setMinutes, setHours } from 'date-fns'
import {
  HistoryDate,
  HistoryDateRequest,
  Observation
} from '@/Network/Types/Requests/StationRequests'
import { UserInfo } from '@/Network/Types/Responses/UserResponse'
import {
  ExceptionSummaryStatus,
  StationSectorRef,
  StationSpecialtyRef
} from '@/Network/Types/Responses/StationResponses'
import type { ShiftDetails } from '@/Network/Types/Responses/StationResponses'
import { CoverageStatus } from '@/Network/Types/Responses/CoverageResponses'
import { CoverageMode } from '@/Network/Types/Requests/CoverageRequests'
import type { CoverageInput } from '@/Network/Types/Requests/CoverageRequests'
import { getStationCoverageInfo } from '@/utils/coverage'
import type { SpecialtySelection } from '@/utils/sectorSpecialty'

export interface StationToUpdate {
  stationId: number
  companyId: number
  doctorId?: number
  date: Date
  shift?: {
    id: number
    name: string
    deletedAt?: Date | null
    createdAt?: Date
    updatedAt?: Date | null
    isActive?: boolean
  }
  startHour?: Date
  endHour?: Date
  timezone: string
  stationType: string
  ourTeam: boolean
  inCash: boolean
  advanceRequested: boolean
  advancePaid: boolean
  modifiedValue?: boolean
  value: number
  checks: HistoryDate[]
  observations?: Observation[]
  doctorPending?: boolean
  urlConviteDoctor?: string
  stationValue: number
  position: any
  healthUnitLatitude: string
  healthUnitLongitude: string
  doctorStationId?: number
  userSponsor?: UserInfo
  scheduleId?: number
  /** Setor/especialidade gravados no plantão (P1). Opcionais: BE anterior à F3 não manda. */
  sector?: StationSectorRef | null
  specialty?: StationSpecialtyRef | null
  schedule?: any
  doctorStation?: {
    id: number
    dsPaymentDoctorFormation: {
      id: number
      name?: string
      deletedAt?: Date | null
      createdAt?: Date
      updatedAt?: Date | null
      isActive?: boolean
    }
    doctor: {
      id?: number
      mainCrm: {
        id: number
        name: string
        status: string
        number: string
        uf: string
        createdAt: Date
        updatedAt: Date | null
        deletedAt: Date | null
        isActive: boolean
      }
      user?: { id: number; name: string; avatarUrl?: string | null }
    }
    type: string
    ourTeam: boolean
    requestAntecipe: boolean
    antecipeWasPaid: boolean
  }
  exceptionStatus?: ExceptionSummaryStatus
  doctorNpsScore?: {
    npsScore: number
    avgScore: number
    evaluations: number
    profile: string
  } | null
  /**
   * F2 — bloqueio do lançamento manual de check, repassado do campo aditivo de
   * `GET /stations/:id`. Ausente (backend anterior à F2), a aba de checks cai no
   * fallback por `registration-settings` do `useDsAttendanceStore`.
   */
  manualCheckBlockEnabled?: boolean
  /** Estado da cobertura (aditivo): ausente com backend anterior à feature. */
  coverageStatus?: CoverageStatus
  coverageResponsibleId?: number | null
  coverageResponsibleName?: string | null
  coverageNote?: string | null
}

/**
 * Pré-preenchimento do "Criar plantão" vindo de outra tela (Timeline no celular: "Criar vaga
 * HH–HH" no buraco). Datas e horas no formato dos campos do formulário. `companyId`/`scheduleId`
 * nulos = o usuário escolhe; a especialidade nunca vem (o usuário escolhe).
 */
export interface CreateStationPrefill {
  companyId: number | null
  healthUnitId: number
  scheduleId: number | null
  /** `dd/MM/yyyy`. */
  date: string
  /** `dd/MM/yyyy`. */
  endDate: string
  /** `HH:mm`. */
  startHour: string
  /** `HH:mm`. */
  endHour: string
  sector: StationSectorRef | null
}

export const optionsRepeatStation = [
  { title: 'Domingo', value: '0' },
  { title: 'Segunda-feira', value: '1' },
  { title: 'Terça-feira', value: '2' },
  { title: 'Quarta-feira', value: '3' },
  { title: 'Quinta-feira', value: '4' },
  { title: 'Sexta-feira', value: '5' },
  { title: 'Sábado', value: '6' }
]

export const finishedAtOptions = [
  { title: 'Não repetir', value: 'never' },
  { title: 'Até o fim da escala', value: 'always' },
  { title: 'Até', value: 'until' },
  { title: 'Após', value: 'occurrence' }
]

export const StationSchema = yup.object({
  // SCHEDULE SECTION
  company: yup.number().integer().nullable(),
  healthUnit: yup.number().integer().nullable(),
  schedule: yup.number().integer().nullable(),
  userSponsorId: yup.number().integer().optional(), // Old: sponsorDoctorId
  // DATE SECTION
  date: yup.string().required(),
  startHour: yup.string().required(),
  endHour: yup.string().required(),
  timezone: yup.string().required(),
  configurationRepetion: yup.string().oneOf(['no-repeat', 'daily', 'custom']).required(),
  valueRepeation: yup.array().when('configurationRepetion', {
    is: 'custom',
    then: yup.array().min(1).required(),
    otherwise: yup.number().nullable().optional()
  }),
  finishedAt: yup.string().oneOf(['never', 'until', 'occurrence', 'always']).required(),
  finishedAtUntil: yup.date().optional(),
  finishedAtAfter: yup.number().when('finishedAt', {
    is: 'occurrence',
    then: yup.number().integer().min(1).required(),
    otherwise: yup.number().nullable().optional()
  }),
  // INFO SECTION
  ourTeam: yup.boolean().required(),
  stationType: yup.string().required(),

  // VALUES SECTION
  doctorId: yup.number().integer().optional(),
  inCash: yup.boolean().required(),
  advanceRequested: yup.boolean().required(),
  advancePaid: yup.boolean().required(),
  urlConviteDoctor: yup.string().optional(),
  value: yup.number().optional(),

  // OBSERVATION SECTION
  observations: yup
    .array()
    .of(
      yup.object({
        date: yup.string().optional(),
        description: yup.string().min(1).required(),
        userName: yup.string().optional()
      })
    )
    .required()
})

export type Station = yup.InferType<typeof StationSchema>
export type ErrorStation = { [K in keyof Station]: string }

export const defaultValues = {
  company: null,
  userSponsorId: undefined, // Old: sponsorDoctorId
  doctorId: undefined,
  startHour: format(new Date(), 'HH:mm'),
  endHour: format(new Date(), 'HH:mm'),
  timezone: '-03:00',
  date: format(new Date(), 'dd/MM/yyyy'),
  configurationRepetion: 'no-repeat',
  finishedAt: 'never',
  finishedAtAfter: undefined,
  finishedAtUntil: new Date(),
  healthUnit: null,
  ourTeam: true,
  value: undefined,
  schedule: undefined,
  stationType: 'FIXO',
  valueRepeation: undefined,
  inCash: false,
  advanceRequested: false,
  urlConviteDoctor: '#',
  advancePaid: false,
  observations: []
}
const values: Station = { ...defaultValues }

export const states = {
  values
}

export const getDate = ({
  date,
  hours,
  timezone = '-03:00'
}: {
  date: string
  hours: string
  timezone?: string
}): Date => {
  const [day, month, year] = date.split('/')
  const [hour, minute] = hours.split(':')
  const formatedDate = `${year}-${month}-${day}T${hour}:${minute}:00.000${timezone}`
  return new Date(formatedDate)
}

export const getStartDateAndEndDate = ({
  date,
  endDateSelected,
  startHour,
  endHour,
  timezone = '-03:00'
}: {
  date: string
  endDateSelected: string
  startHour: string
  endHour: string
  timezone?: string
}): { startDate: Date; endDate: Date } => {
  const startDate = getDate({ date, hours: startHour, timezone })
  const endDate = getDate({ date: endDateSelected, hours: endHour, timezone })

  return { startDate, endDate }
}

export const hourDate = (data: Date, text: string) => {
  const hour = Number(text.split(':')[0])
  const minutes = Number(text.split(':')[1])
  const res = setMinutes(setHours(data, hour), minutes)
  return res
}

export const getHistoriesRequest = (
  data: HistoryDate[],
  startDate: Date,
  endDate: Date
): HistoryDateRequest[] => {
  const payload = data.map((c): HistoryDateRequest => {
    let checkin = c.checkin
    let checkout = c.checkout
    if (c.checkin.split('T').length > 1) {
      const hours = new Date(c.checkin).getHours()
      const minutes = new Date(c.checkin).getMinutes()
      checkin = `${hours}:${minutes}`
    }

    if (c.checkout && c.checkout.split('T').length > 1) {
      const hours = new Date(c.checkout).getHours()
      const minutes = new Date(c.checkout).getMinutes()
      checkout = `${hours}:${minutes}`
    }

    // `isMain` deixou de viajar no request (BF5): o servidor decide o check
    // principal em `ensureMainCheck` e ignora o campo — mandá-lo sugeriria um
    // controle que a UI não tem mais.
    const historyRequest: HistoryDateRequest = {
      id: c.id,
      checkin: hourDate(startDate, checkin),
      checkout: checkout ? hourDate(endDate, checkout) : undefined,
      origin: c.origin
    }

    if (c.checkEvidences && c.checkEvidences.length > 0) {
      historyRequest.checkEvidences = c.checkEvidences
    }

    return historyRequest
  })

  return payload
}

type RecurrenceParams = {
  date: string
  finishedAt: string
  finishedAtAfter: number
  finishedAtUntil: string
  repetionDays: Record<string, string>
}

const parseBrDate = (value: string): Date | null => {
  if (!value) return null
  const parsed = parse(value, 'dd/MM/yyyy', new Date())
  return isValid(parsed) ? parsed : null
}

/** Espelho de `getStopDate` do BE; `null` quando não dá para saber o fim. */
function getRecurrenceStopDate(start: Date, params: RecurrenceParams): Date | null {
  switch (params.finishedAt) {
    case 'occurrence':
      return addWeeks(start, Number(params.finishedAtAfter) || 1)
    case 'always':
      return addYears(start, 2)
    case 'never':
      return addWeeks(start, 1)
    case 'until':
      return parseBrDate(params.finishedAtUntil)
    default:
      return null
  }
}

/**
 * Estimativa do nº de plantões da repetição, espelhando o laço do BE
 * (`stations.service.ts#createRecurringStationFlow` + `getStopDate`: do dia inicial até o
 * dia de parada, inclusive; posições extras do dia somam). É teto: o BE ainda descarta
 * duplicados (Q31), por isso o texto diz "Até N".
 */
export function estimateRecurringStationCount(params: RecurrenceParams): number | null {
  const start = parseBrDate(params.date)
  if (!start) return null
  const stop = getRecurrenceStopDate(start, params)
  if (!stop) return null
  let total = 0
  for (let day = start; day.getTime() <= stop.getTime(); day = addDays(day, 1)) {
    const qty = Number(params.repetionDays[String(day.getDay())] ?? 0)
    if (qty > 0) total += qty
  }
  return total
}

export function buildSectorSpecialtySummary(
  count: number | null,
  sector: StationSectorRef | null,
  specialty: SpecialtySelection | null
): string {
  const sectorText = sector ? `com setor ${sector.name}` : 'sem setor'
  // "com setor X e especialidade Y" | "sem setor e com especialidade Y"
  const specialtyText = specialty
    ? `${sector ? '' : 'com '}especialidade ${specialty.name}`
    : 'sem especialidade'
  const both = `${sectorText} e ${specialtyText}`
  return count !== null
    ? `Até ${count} plantões serão criados ${both}.`
    : `Todos os plantões gerados pela repetição serão criados ${both}.`
}

type CoverageUpdateFields = Pick<
  StationToUpdate,
  'coverageStatus' | 'coverageResponsibleId' | 'coverageResponsibleName' | 'coverageNote'
>

/** Campos de cobertura do `GET /stations/:id` para o `StationToUpdate` — vazio com BE antigo. */
export function mapCoverageToStationUpdate(
  details: Pick<ShiftDetails, 'doctorStation'> | null | undefined
): CoverageUpdateFields {
  const info = getStationCoverageInfo(details)
  if (!info.hasContract) return {}
  return {
    coverageStatus: info.status,
    coverageResponsibleId: info.responsibleId,
    coverageResponsibleName: info.responsibleName,
    coverageNote: info.note
  }
}

/**
 * E-06 / Q31: a edição unitária passa a aceitar OPEN e UNIDENTIFIED (horário/valor).
 * Mandar o estado explícito evita que o backend leia "sem médico" como remoção.
 */
export function buildUpdateCoverageInput(
  infos: Pick<StationToUpdate, 'coverageStatus' | 'coverageResponsibleId' | 'coverageNote'>
): CoverageInput | null {
  if (infos.coverageStatus === CoverageStatus.UNIDENTIFIED) {
    return {
      coverage: CoverageMode.UNIDENTIFIED,
      coverageResponsibleId: infos.coverageResponsibleId ?? undefined,
      ...(infos.coverageNote ? { coverageNote: infos.coverageNote } : {})
    }
  }
  if (infos.coverageStatus === CoverageStatus.OPEN) return { coverage: CoverageMode.OPEN }
  return null
}
